'use client';

import {
  Building2, Receipt, FileKey2, Mail, FileText, Bell, Shield,
  Palette, Sparkles, CreditCard, LayoutDashboard, Gift, User, SlidersHorizontal,
  Users, Code, Truck, Monitor, PackagePlus, Trash2, ScrollText, Upload, Plug, Database,
  Package, FolderOpen, Landmark, Hash, Percent, Ruler, UserCheck, UserCog, ShieldCheck, Warehouse, Tags, Library,
  ShoppingBag, Car,
} from 'lucide-react';
import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ScrollableTabRow } from '@/shared/components/scrollable-tab-row';
import type { Permission } from '@softfactura/shared';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import { DREPT_PAGINA_IMPORT } from '@/features/migration/import-permissions';
import { EfacturaIcon } from '@/shared/components/icons/efactura-icon';

export type NavItem = {
  id: string;
  label: string;
  icon: any;
  adminOnly?: boolean;
  href?: string;
  /** Optional restriction: show only when the company's legalForm is in this list. */
  legalForms?: readonly string[];
  /**
   * Optional permission(s) required to see this item. If an array, the user
   * needs at least one (OR semantics — same as `<PermissionGate anyOf>`).
   * Mirrors the page-level TAB_PERMISSION map; single source of truth lives
   * here so the nav doesn't surface tabs that would just render NoAccessPanel.
   */
  permission?: Permission | Permission[];
  /** Optional badge displayed next to label */
  badge?: { text: string; tone?: 'neutral' | 'amber' | 'green' | 'red' };
};

export type NavGroup = {
  label: string;
  /** Pictograma afisata in top-nav orizontal — opt. pt backward compat cu consumatori vechi. */
  icon?: any;
  items: readonly NavItem[];
};

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Firmă', icon: Building2,
    items: [
      { id: 'company', label: 'Date firmă', icon: Building2, permission: 'settings.company' },
      { id: 'fiscal', label: 'Fiscalitate', icon: Receipt, permission: 'settings.fiscal' },
      { id: 'team', label: 'Echipa', icon: Users, permission: 'team.read' },
      { id: 'firmele-mele', label: 'Firmele mele', icon: Building2, permission: 'billing.manage' },
      { id: 'billing', label: 'Abonament', icon: CreditCard, permission: 'billing.read' },
      { id: 'capabilities', label: 'Excluse din configurare', icon: SlidersHorizontal, permission: 'team.customRoles' },
    ],
  },
  {
    label: 'Personalizare', icon: Palette,
    items: [
      { id: 'document-prefs', label: 'Setări documente', icon: SlidersHorizontal, permission: 'settings.invoicing' },
      { id: 'reception-prefs', label: 'Recepții', icon: PackagePlus, permission: 'settings.invoicing' },
      { id: 'traceability', label: 'Loturi & serii', icon: ScrollText, permission: 'settings.invoicing' },
      { id: 'pdf', label: 'Șabloane PDF', icon: FileText, permission: 'settings.invoicing' },
      { id: 'email', label: 'Email', icon: Mail, permission: 'settings.company' },
    ],
  },
  {
    label: 'Integrări', icon: Plug,
    items: [
      { id: 'efactura', label: 'e-Factura', icon: EfacturaIcon, permission: 'efactura.config' },
      { id: 'f082', label: 'Formular 082 (PFA)', icon: FileKey2, permission: 'settings.fiscal', legalForms: ['PFA', 'II', 'IF'], badge: { text: 'PFA', tone: 'neutral' } },
      { id: 'etransport', label: 'e-Transport', icon: Truck, permission: 'etransport.read', badge: { text: 'ANAF', tone: 'neutral' } },
      { id: 'pos', label: 'POS & Casă', icon: Monitor, permission: 'pos.config' },
      { id: 'curieri', label: 'Curieri', icon: Truck, permission: 'couriers.config' },
      { id: 'payments', label: 'Plăți online', icon: CreditCard, permission: 'couriers.config' },
      // Magazine online (marketplace): conectarea conturilor de seller (eMAG,
      // Fashion Days, Trendyol...) — credentialele eMAG mutate aici (cerinta user)
      { id: 'marketplace', label: 'Magazine online', icon: ShoppingBag, permission: 'couriers.config' },
      // Transport alternativ (ride-sharing): punct de ACTIVARE — sidebar-ul ascunde butonul
      // „Ride-sharing" pt firmele fără config/conexiune (hasRideHailingPresence, #audit 2026-07-12).
      { id: 'transport-alternativ', label: 'Transport alternativ', icon: Car, permission: 'rideHailing.read' },
      { id: 'developers', label: 'API & Webhooks', icon: Code, permission: ['developer.apiKeys.read', 'developer.webhooks.read'], badge: { text: 'Tehnic', tone: 'neutral' } },
    ],
  },
  {
    label: 'Cont', icon: User,
    items: [
      { id: 'profil', label: 'Profilul meu', icon: User },
      { id: 'security', label: 'Securitate', icon: Shield },
      { id: 'notifications', label: 'Notificări', icon: Bell, permission: 'team.read' },
      { id: 'appearance', label: 'Aspect', icon: Palette, permission: 'settings.appearance' },
      { id: 'dashboard-prefs', label: 'Dashboard', icon: LayoutDashboard, permission: 'settings.appearance' },
      { id: 'referral', label: 'Recomandări', icon: Gift, permission: 'billing.read', badge: { text: 'Nou', tone: 'green' } },
      { id: 'briefing-debug', label: 'Sumar · feedback', icon: Sparkles, permission: 'team.customRoles' },
    ],
  },
  {
    label: 'Nomenclatoare', icon: Library,
    items: [
      { id: 'produse', label: 'Produse & Servicii', icon: Package },
      { id: 'grupuri', label: 'Grupuri clienți', icon: FolderOpen },
      { id: 'conturi-bancare', label: 'Conturi bancare', icon: Landmark },
      { id: 'serii', label: 'Serii documente', icon: Hash },
      { id: 'cote-tva', label: 'Cote TVA', icon: Percent },
      { id: 'unitati-masura', label: 'Unități măsură', icon: Ruler },
      { id: 'delegati', label: 'Delegați', icon: UserCheck },
      { id: 'emitenti', label: 'Emitenți', icon: UserCog },
      { id: 'gestionari', label: 'Gestionari', icon: ShieldCheck, permission: 'stock.read' },
      { id: 'depozite', label: 'Depozite', icon: Warehouse, permission: 'stock.read' },
      { id: 'categorii-cheltuieli', label: 'Categorii cheltuieli', icon: Tags },
    ],
  },
  {
    label: 'Import / Migrare', icon: Database,
    items: [
      // importul cere pe server `billing.manage` (MigrationController / MigrationV2Controller)
      { id: 'migrare', label: 'Import date', icon: Upload, permission: DREPT_PAGINA_IMPORT },
      { id: 'cos-de-gunoi', label: 'Coș de gunoi', icon: Trash2 },
      { id: 'audit-log', label: 'Jurnal activitate', icon: ScrollText, permission: 'team.read' },
    ],
  },
];

// Flat list (for tab id lookups)
export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

interface SettingsNavProps {
  /** ID-ul activ — fie un tab id ('company', etc.), fie 'profil' pentru pagina profile */
  activeId: string;
  /** Callback when a tab (non-href item) is clicked. If undefined, all items render as Link. */
  onTabChange?: (id: string) => void;
  /** User role — pentru a filtra adminOnly items */
  userRole?: string;
  /** Forma juridică a firmei curente — pentru a filtra items cu legalForms (ex. f082 doar PFA/II/IF) */
  legalForm?: string;
  /** Map of tab id → has warning (shows amber dot/badge) */
  warnings?: Record<string, boolean>;
  /** Tab IDs to hide entirely (data-driven; e.g. 'capabilities' when no excluded items). */
  hiddenIds?: readonly string[];
}

export function SettingsNav({ activeId, onTabChange, userRole, legalForm, warnings, hiddenIds }: SettingsNavProps) {
  const { can, canAny } = usePermissions();

  // Filtru de vizibilitate per item (permission + legalForm + hiddenIds)
  const canSeeItem = (item: NavItem): boolean => {
    if (item.permission) {
      const allowed = Array.isArray(item.permission) ? canAny(item.permission) : can(item.permission);
      if (!allowed) return false;
    }
    if (item.adminOnly && userRole !== 'owner' && userRole !== 'admin') return false;
    if (item.legalForms && (!legalForm || !item.legalForms.includes(legalForm))) return false;
    if (hiddenIds && hiddenIds.includes(item.id)) return false;
    return true;
  };

  // Grupurile vizibile (cele care au cel putin un item vizibil pt user-ul curent)
  const visibleGroups = useMemo(
    () => NAV_GROUPS
      .map((g) => ({ ...g, items: g.items.filter(canSeeItem) }))
      .filter((g) => g.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId, userRole, legalForm, hiddenIds, warnings],
  );

  // Grupul care contine tabul activ (sursa de adevar pt context vizual)
  const activeGroupIdx = useMemo(() => {
    const i = visibleGroups.findIndex((g) => g.items.some((it) => it.id === activeId));
    return i === -1 ? 0 : i;
  }, [visibleGroups, activeId]);

  const router = useRouter();

  // Click pe header de grup: navigare la primul item din grup. No-op daca user-ul
  // e deja in acest grup (evitam aruncatul utilizatorului inapoi pe primul tab cand
  // apasa accidental pe headerul grupului curent).
  const handleGroupClick = (idx: number) => {
    if (idx === activeGroupIdx) return;
    const firstItem = visibleGroups[idx]?.items[0];
    if (!firstItem) return;
    if (firstItem.href) router.push(firstItem.href);
    else if (onTabChange) onTabChange(firstItem.id);
  };

  // Sub-bar afiseaza intotdeauna items-ul grupului activ (1:1 cu tabul curent).
  const currentItems = visibleGroups[activeGroupIdx]?.items ?? [];

  return (
    // ⚠ Acest sub-nav e sticky (2 rânduri, ~83px). Preview-ul sticky din
    // appearance-tab.tsx (`top-[88px]`) îl degajă ca să nu acopere preview-ul la
    // scroll — dacă adaugi un rând / mărești padding-ul / textul aici, ajustează
    // acolo offset-ul corespunzător.
    <nav aria-label="Configurări" className="sticky -top-2 sm:-top-3 z-30 -mt-2 sm:-mt-3 -mx-2 sm:-mx-3 px-2 sm:px-3 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
      {/* Randul 1: grupurile top */}
      <ScrollableTabRow>
        {visibleGroups.map((g, idx) => {
          const isActiveGroup = idx === activeGroupIdx;
          const Icon = g.icon;
          return (
            <button
              type="button"
              key={g.label}
              onClick={() => handleGroupClick(idx)}
              aria-current={isActiveGroup ? 'page' : undefined}
              className={
                'inline-flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-[13.5px] border-b-2 transition-colors ' +
                (isActiveGroup
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
                  : 'border-transparent text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium')
              }
            >
              {Icon && <Icon className={'h-3.5 w-3.5 ' + (isActiveGroup ? 'opacity-100' : 'opacity-70')} />}
              {g.label}
            </button>
          );
        })}
      </ScrollableTabRow>

      {/* Randul 2: sub-bar persistenta cu items grupului activ — stil consistent cu top tabs (border-b portocaliu pe activ) */}
      <ScrollableTabRow className="bg-neutral-50/40 dark:bg-neutral-900/20 border-t border-neutral-100 dark:border-neutral-800/60">
        {currentItems.map((tab) => {
          const isActive = activeId === tab.id;
          const isLink = !!tab.href || !onTabChange;
          const href = isLink ? (tab.href || `/setari?tab=${tab.id}`) : undefined;
          const Icon = tab.icon;
          const className =
            'inline-flex items-center gap-2 whitespace-nowrap px-3 py-2 text-[12.5px] border-b-2 transition-colors ' +
            (isActive
              ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
              : 'border-transparent text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium');
          const content = (
            <>
              {Icon && <Icon className={'h-3.5 w-3.5 ' + (isActive ? 'opacity-100' : 'opacity-70')} />}
              <span>{tab.label}</span>
              {warnings?.[tab.id] && !tab.badge && <NavBadge text="!" tone="amber" />}
              {tab.badge && <NavBadge text={tab.badge.text} tone={tab.badge.tone} />}
            </>
          );
          if (isLink) {
            return (
              <Link key={tab.id} href={href!} className={className}>
                {content}
              </Link>
            );
          }
          return (
            <button key={tab.id} type="button" onClick={() => onTabChange!(tab.id)} className={className}>
              {content}
            </button>
          );
        })}
      </ScrollableTabRow>
    </nav>
  );
}

function NavBadge({ text, tone = 'neutral' }: { text: string; tone?: 'neutral' | 'amber' | 'green' | 'red' }) {
  const toneClass = {
    neutral: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-900',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-900',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-900',
  }[tone];
  return (
    <span className={'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide ' + toneClass}>
      {text}
    </span>
  );
}
